import { ReactNode, useCallback } from 'react';
import { Box, Stack, SxProps, useTheme } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '../../CloudUploadIcon';
import { VigoTypography } from 'src/@vigo/vigo-typography';

type Mode = 'overlay' | 'wrapper';
export type AllowedFileTypes = 'CSV' | 'XML';

export interface ImportDropzoneProps {
  mode?: Mode;
  children?: ReactNode;
  onFileDropped?: (file: File[]) => void;
  wrapperSx?: SxProps;
  dragOverlaySx?: SxProps;
  disabled?: boolean;
  allowedFileTypes?: AllowedFileTypes[];
}

const ImportDropzone = ({
  mode = 'wrapper',
  children,
  onFileDropped,
  wrapperSx = {},
  dragOverlaySx = {},
  disabled = false,
  allowedFileTypes = []
}: ImportDropzoneProps) => {
  const theme = useTheme();

  const onDrop = useCallback((acceptedFiles: File[]) => {
    onFileDropped?.(acceptedFiles);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const acceptExtensions = allowedFileTypes.reduce<Record<string, string[]>>((acc, type) => {
    switch (type) {
      case 'CSV':
        acc['text/csv'] = ['.csv'];
        break;
      case 'XML':
        acc['application/xml'] = ['.xml'];
        acc['text/xml'] = ['.xml'];
        break;
      default:
        break;
    }

    return acc;
  }, {});

  const { getRootProps, getInputProps, isDragActive, isDragAccept } = useDropzone({
    noDragEventsBubbling: true,
    onDrop,
    noClick: true,
    disabled: disabled,
    noKeyboard: true,
    accept: acceptExtensions
  });

  return (
    <Box
      component={'div'}
      sx={{ position: mode == 'wrapper' ? 'relative' : 'absolute', ...wrapperSx }}
      {...getRootProps()}>
      <input {...getInputProps()} />

      {children}

      {isDragActive && isDragAccept && (
        <Stack
          direction="column"
          alignItems={'center'}
          justifyContent={'center'}
          sx={{
            ...(mode == 'wrapper' && { position: 'absolute' }),
            width: '100%',
            height: '100%',
            top: 0,
            left: 0,
            borderRadius: '4px',
            border: '1px dashed #ACACAC',
            background: '#F7F7F7',
            ...dragOverlaySx
          }}>
          <CloudUploadIcon
            fill={isDragActive && isDragAccept ? theme.palette.customColors.primaryGradient : '#626679'}
          />
          <VigoTypography
            sx={{
              ...(isDragActive && isDragAccept && { color: theme => theme.palette.customColors.primaryGradient })
            }}
            value="Drag & drop your file here to upload"
          />
        </Stack>
      )}
    </Box>
  );
};

export default ImportDropzone;
